import { useCallback, useEffect, useState } from 'react';
import type React from 'react';

import { Button } from '@change/ds-core-ui';
import { SignaturesIcon } from '@change/ds-icons';

import { Translate } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import {
	useMobileNativeShare,
	useShareCtaPlacementEnabled,
	useSignatureTracking,
} from '#src/app/pages/petitionDetails/shared/hooks';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';

import { ActionButtonWrapper } from './ActionButtonWrapper';
import type { ActionPanelVariant } from './MobileActionButton';
import { MobileSignModal, usePreloadMobileSignModal } from './MobileSignModal';
import { useMobileModalContext } from './shared/useMobileModalContext';
import { useShowSignForm } from './shared/useShowSignForm';
import { SignCtaShareAffordance } from './SignCtaShareAffordance';

type Props = {
	hideShareAffordance?: boolean;
	sticky: boolean;
	visible: boolean;
	variant: ActionPanelVariant;
};

/**
 * `shareCtaPlacementEnabled` already folds in `!hideShareAffordance` (see below), so it's not
 * re-checked here.
 */
function shouldTrackShareCtaCopyExperiment(
	sticky: boolean,
	hasShareAction: boolean,
	shareCtaPlacementEnabled: boolean,
) {
	return !sticky && hasShareAction && shareCtaPlacementEnabled;
}

export const MobileSignButtonContainer: React.FC<Props> = ({
	hideShareAffordance = false,
	sticky,
	visible,
	variant,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const { trackMobileSignIntent } = useSignatureTracking();

	const context = useMobileModalContext();

	usePreloadMobileSignModal();

	const {
		data: {
			experiments: { petitionDetailsShareCtaCopyExperiment },
		},
	} = usePetitionDetailsPageUserData();
	const {
		data: {
			petition: { id: petitionId },
		},
	} = usePetitionDetailsPageData();

	// Render site, derived from the existing `sticky` prop — both instances render the affordance,
	// so this has no `sticky` gate; `sticky` only decides the tracking context value.
	const trackingContext = sticky ? 'petition_show_sign_cta_sticky' : 'petition_show_sign_cta_inline';

	// Evaluated in every arm, control included: this is the availability half of the render/exposure
	// predicate, and it must be arm-independent so the compared populations are identical.
	const shareAction = useMobileNativeShare(trackingContext);
	const shareCtaPlacementEnabled = useShareCtaPlacementEnabled() && !hideShareAffordance;
	const trackShareCtaCopyExperiment = shouldTrackShareCtaCopyExperiment(
		sticky,
		!!shareAction,
		shareCtaPlacementEnabled,
	);
	// resetKeys is required, not optional: this component instance persists across an SPA
	// petition-to-petition navigation (no remount), and variation/experimentName are otherwise
	// unchanged, so without the petition id the new petition's legitimate exposure would be deduped
	// against the previous one. Same requirement as useTreatSignFormAddressRemovalExperiment on this page.
	const trackShareCtaCopyExposure = useTrackExperimentExposure(petitionDetailsShareCtaCopyExperiment, {
		resetKeys: [petitionId],
	});

	// Fired from a stable effect on this component instance, not a conditionally-mounted
	// <TrackExperimentExposure>: `shareAction` resolves asynchronously (see useMobileNativeShare,
	// which queries on `sessionUuid`) and can flicker unavailable → available after mount. A
	// conditionally-mounted tracker would unmount/remount on that flicker, resetting its per-mount
	// dedup flag (useTrackOnce) and double-firing $exposure — this component instance persists across
	// the flicker, so calling the hook's returned function here keeps the dedup flag stable instead.
	// Both instances of MobileActionButtonContainer render this component; `!sticky` in
	// shouldTrackShareCtaCopyExperiment (same guard as MobileSignButtonAutoOpen above) is what
	// prevents the *other* double-fire vector, across the two sibling mounts.
	useEffect(() => {
		if (trackShareCtaCopyExperiment) {
			void trackShareCtaCopyExposure();
		}
	}, [trackShareCtaCopyExperiment, trackShareCtaCopyExposure]);

	const openModal = useCallback(() => {
		trackMobileSignIntent();
		setIsOpen(true);
	}, [trackMobileSignIntent]);

	const openModalWithoutTracking = useCallback(() => {
		setIsOpen(true);
	}, []);

	const signButton = (
		<Button
			variant="primary"
			onClick={openModal}
			leadingIcon={<SignaturesIcon size="small" />}
			data-qa="mobile-sign-button"
			data-testid="mobile-sign-button"
			data-gtm="ga4-sign-button"
			size="large"
			className="w-full max-w-none"
		>
			<Translate value="corgi.petition_gamma.petition_sign.action" />
		</Button>
	);

	return (
		<>
			{/* we're looking at breakpoints to decide whether to open the modal, which cannot be done server-side */}
			{/* Only auto-open on the non-sticky instance to avoid opening modal twice (both sticky and non-sticky render this component) */}
			{!sticky && (
				<ClientRender>
					<MobileSignButtonAutoOpen onOpen={openModalWithoutTracking} />
				</ClientRender>
			)}
			{/* eslint-disable-next-line @change-corgi/require-data-qa-attribute */}
			<ActionButtonWrapper visible={visible} sticky={sticky}>
				{shareCtaPlacementEnabled ? (
					<div className="flex flex-col gap-2">
						{signButton}
						<SignCtaShareAffordance
							shareAction={shareAction}
							variation={petitionDetailsShareCtaCopyExperiment.variation}
						/>
					</div>
				) : (
					signButton
				)}
			</ActionButtonWrapper>

			<MobileSignModal context={context} isOpen={isOpen} setIsOpen={setIsOpen} variant={variant} />
		</>
	);
};
function MobileSignButtonAutoOpen({ onOpen }: { onOpen: () => void }) {
	const showSignForm = useShowSignForm();

	useEffect(() => {
		if (showSignForm) {
			/**
			 * There are some updates that happen right before showing the modal (such as populating the form fields)
			 * that need to complete first so that the modal opens with the correct data.
			 * Using setTimeout to defer the onOpen call to the next event loop iteration and avoid a race condition.
			 */
			setTimeout(() => {
				onOpen();
			}, 0);
		}
		// disabling dependency check to avoid attempting to open the modal multiple times
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
}
